import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type DiscoverySortOption = 'nearest' | 'most-klyxd' | 'recently-active' | 'newest' | 'highest-value';
export type LocationType = 'home' | 'touring' | 'both';
export type ActivityFilter = '24h' | '7d' | '30d' | null;

export interface DiscoveryProfile {
  id: number;
  username: string;
  displayName: string | null;
  avatar: string | null;
  bio: string | null;
  location: string;
  distance: string;
  distanceValue: number;
  profile: {
    nftProfileId: number;
    priceKlv: string;
    isListed: boolean;
    isOwner: boolean;
  };
  stats: {
    tradeCount: number;
    favoriteCount: number;
    lastTradeAt: string | null;
  };
  ownership: {
    iOwn: boolean;
    iKlyxd: boolean;
  };
  badges: {
    verified: boolean;
    top: boolean;
    trending: boolean;
  };
  interests: string[];
  matchedInterests: string[];
}

export interface DiscoveryFilters {
  radius: number; // 10-250
  unit: 'miles' | 'km';
  search: string;
  sort: DiscoverySortOption;
  locationType: LocationType;
  activity: ActivityFilter;
  badges: string[]; // ['verified', 'top', 'trending']
  iOwn: boolean;
  iKlyxd: boolean;
  interests: string[];
}

export interface PaginationState {
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

interface DiscoveryState {
  profiles: DiscoveryProfile[];
  spotlightProfiles: DiscoveryProfile[];
  filters: DiscoveryFilters;
  pagination: PaginationState;
  isLoading: boolean;
  isLoadingMore: boolean;
  isLoadingSpotlight: boolean;
  error: string | null;
  hasLocation: boolean;
  showEmptyState: boolean;
}

const initialState: DiscoveryState = {
  profiles: [],
  spotlightProfiles: [],
  filters: {
    radius: 250,
    unit: 'miles',
    search: '',
    sort: 'nearest',
    locationType: 'both',
    activity: null,
    badges: [],
    iOwn: false,
    iKlyxd: false,
    interests: [],
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    hasMore: false,
  },
  isLoading: false,
  isLoadingMore: false,
  isLoadingSpotlight: false,
  error: null,
  hasLocation: true,
  showEmptyState: false,
};

const discoverySlice = createSlice({
  name: 'discovery',
  initialState,
  reducers: {
    setProfiles: (state, action: PayloadAction<DiscoveryProfile[]>) => {
      state.profiles = action.payload;
      state.error = null;
      state.showEmptyState = action.payload.length === 0;
      // Reset to page 1 when setting new profiles (not appending)
      state.pagination.page = 1;
    },

    appendProfiles: (state, action: PayloadAction<DiscoveryProfile[]>) => {
      state.profiles = [...state.profiles, ...action.payload];
      state.error = null;
    },

    setSpotlightProfiles: (state, action: PayloadAction<DiscoveryProfile[]>) => {
      state.spotlightProfiles = action.payload;
    },

    updateFilter: <K extends keyof DiscoveryFilters>(
      state: DiscoveryState,
      action: PayloadAction<{ key: K; value: DiscoveryFilters[K] }>
    ) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
      // Don't clear profiles here - let the search thunk handle it
    },

    setFilters: (state, action: PayloadAction<Partial<DiscoveryFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      // Don't clear profiles here - let the search thunk handle it
      // This prevents profiles from disappearing while dragging the slider
    },

    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.profiles = [];
      state.pagination.page = 1;
      state.showEmptyState = false;
    },

    setPagination: (state, action: PayloadAction<Partial<PaginationState>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },

    nextPage: (state) => {
      if (state.pagination.hasMore && state.pagination.page * state.pagination.limit < 1000) {
        state.pagination.page += 1;
      }
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setLoadingMore: (state, action: PayloadAction<boolean>) => {
      state.isLoadingMore = action.payload;
    },

    setLoadingSpotlight: (state, action: PayloadAction<boolean>) => {
      state.isLoadingSpotlight = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
      state.isLoadingMore = false;
      // Show empty state if error is about missing location
      if (action.payload?.includes('Location required')) {
        state.hasLocation = false;
        state.showEmptyState = true;
      }
    },

    setHasLocation: (state, action: PayloadAction<boolean>) => {
      state.hasLocation = action.payload;
    },

    clearProfiles: (state) => {
      state.profiles = [];
      state.pagination.page = 1;
      state.showEmptyState = false;
    },

    // Optimistic flip of ownership.iKlyxd + favoriteCount on a profile,
    // applied to both the main list and the spotlight rail. Matches on
    // profile.profile.nftProfileId because that's the same key the
    // server uses (app.profile_favorite.profile_id → nft_profile.id).
    toggleDiscoveryFavorite: (
      state,
      action: PayloadAction<{ profileId: string; favorited: boolean }>,
    ) => {
      const { profileId, favorited } = action.payload;
      for (const list of [state.profiles, state.spotlightProfiles]) {
        const target = list.find(
          (p) => String(p.profile?.nftProfileId) === String(profileId),
        );
        if (!target) continue;
        if (target.ownership) target.ownership.iKlyxd = favorited;
        const delta = favorited ? 1 : -1;
        target.stats.favoriteCount = Math.max(0, (target.stats.favoriteCount || 0) + delta);
      }
    },

    resetState: () => initialState,
  },
});

export const {
  setProfiles,
  appendProfiles,
  setSpotlightProfiles,
  updateFilter,
  setFilters,
  resetFilters,
  setPagination,
  nextPage,
  setLoading,
  setLoadingMore,
  setLoadingSpotlight,
  setError,
  setHasLocation,
  clearProfiles,
  toggleDiscoveryFavorite,
  resetState,
} = discoverySlice.actions;

export default discoverySlice.reducer;
